import React, { useCallback, useEffect, useState } from "react";
import { Alert, AlertIcon, Box, Button, HStack, Spinner, Text } from "@chakra-ui/react";
import { ArrowsClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getTrail } from "../api/dataMappingClient";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import EsgAdminOnly from "context/Auth/EsgAdminOnly";
import { RichTextViewer } from "../../ISSB/IssbDataInput/components/RichTextEditor/RichTextEditor";
import { decisionsOf, escapeHtml, isWriteStep } from "./stepDecisions";

// Same cap as the backend's render_task_summary (data-sorting/backend/app/session/trail.py) — a long
// count is a signal, not something to read line by line.
const MAX_SUMMARY_STEPS = 20;

/**
 * Client-side twin of `render_task_summary` (data-sorting/backend/app/session/trail.py), built from the
 * raw `steps` the `/trail` endpoint already returns today.
 *
 * The endpoint also computes this same HTML server-side as `methodologySummaryHtml` — the two must never
 * disagree, since they're the same algorithm over the same steps — but a deployed `data-sorting` service
 * can lag behind this frontend build (separate GitLab pipeline), and `steps` was already part of its
 * response before that field existed. Deriving it here means the "Transformations applied" list works
 * against whatever is currently deployed, using data that's already there, rather than waiting on a
 * backend redeploy. `WorkingDataHistoryPanel` prefers the server's own HTML when present and only falls
 * back to this once that field ships everywhere, this function is simply never reached.
 */
const deriveMethodologyHtml = (steps, sourceFileName) => {
  const parts = [];
  if (sourceFileName) {
    parts.push(`<p><strong>Source</strong>: ${escapeHtml(sourceFileName)}</p>`);
  }

  const writes = (steps || []).filter(isWriteStep);
  const decisions = decisionsOf(writes);

  if (!decisions.length) {
    parts.push(
      "<p>No transformation was applied — the data was committed as it arrived from the source file.</p>"
    );
    return parts.join("");
  }

  const shown = decisions.slice(0, MAX_SUMMARY_STEPS);
  const hidden = decisions.length - shown.length;
  const items = shown
    .map((step) => `<li>${escapeHtml(step.note || step.title || step.tool || "step")}</li>`)
    .join("");
  parts.push(`<p><strong>Transformations applied</strong></p><ol>${items}</ol>`);
  if (hidden > 0) {
    parts.push(`<p>…and ${hidden} further step${hidden === 1 ? "" : "s"}.</p>`);
  }
  return parts.join("");
};

/**
 * Right-sidebar panel behind Working Data's "History" button. Shows the same two documents the Commit
 * stage shows — "Methodology written onto the task" and "Trail stored with this commit" — live, at any
 * point before commit, fetched fresh from GET /sessions/{id}/trail rather than from a commit payload.
 *
 * Fetches on mount rather than via React Query: the session's step log changes as the user keeps chatting,
 * so there is nothing here worth caching (same reasoning as ReportDocEditorPanel's DocReportHistoryPanel).
 * A manual Refresh button covers reopening the tray without closing it first — clicking History again while
 * it's already the sidebar content re-renders this same component in place rather than remounting it, so a
 * mount-only fetch would go stale.
 */
export default function WorkingDataHistoryPanel({ sessionId, sourceFileName }) {
  const [trail, setTrail] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTrail = useCallback(() => {
    setLoading(true);
    setError(null);
    getTrail(sessionId)
      .then(setTrail)
      .catch(() => setError("Could not load the transform history."))
      .finally(() => setLoading(false));
  }, [sessionId]);

  useEffect(() => {
    loadTrail();
  }, [loadTrail]);

  const methodologyHtml =
    trail?.methodologySummaryHtml || deriveMethodologyHtml(trail?.steps, sourceFileName);

  return (
    <Box>
      <HStack justify="space-between" align="center">
        <RightSidebarHeader Icon={ClockCounterClockwise}>Transform history</RightSidebarHeader>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<ArrowsClockwise size={14} weight="bold" />}
          onClick={loadTrail}
          isLoading={isLoading}
          flexShrink={0}
        >
          Refresh
        </Button>
      </HStack>

      {isLoading && !trail && (
        <HStack justify="center" py={8}>
          <Spinner size="sm" color="darkTeal" />
        </HStack>
      )}

      {error && (
        <Alert status="error" borderRadius="md" mt={3} fontSize="13px">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {trail && (
        <Box mt={4}>
          <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>
            Transformations applied
          </Text>
          <Box
            p={3}
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            fontSize="12px"
          >
            <RichTextViewer content={methodologyHtml} />
          </Box>

          <EsgAdminOnly>
            <Text fontSize="13px" fontWeight="600" color="gray.600" mt={5} mb={2}>
              Full audit trail (ESG Admin)
            </Text>
            <Box
              p={3}
              maxH="300px"
              overflowY="auto"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              fontSize="12px"
              color="gray.700"
              sx={{
                "& h1, & h2, & h3": { fontWeight: "700", color: "darkTeal", mt: 3, mb: 1 },
                "& p": { mb: 2 },
                "& ul, & ol": { pl: 5, mb: 2 },
                "& code": { fontFamily: "mono", bg: "gray.200", px: 1, borderRadius: "sm" },
                "& table": { width: "100%", mb: 2 },
                "& th, & td": { border: "1px solid", borderColor: "gray.200", px: 2, py: 1, textAlign: "left" },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{trail.markdown}</ReactMarkdown>
            </Box>
          </EsgAdminOnly>
        </Box>
      )}
    </Box>
  );
}
